/**
 * The daily challenge (spec section 4, phase 6).
 *
 * One global seed per day per tier, and ONE attempt — enforced server-side,
 * which is the only enforcement that counts. The localStorage check here exists
 * so a returning player sees their result instead of a start button; it is a
 * courtesy, not a lock, and the screen still lets them try (the gateway will
 * answer `daily_already_played` if they do).
 */

import { TIERS, TIER_CONFIG, countryPromptIsClue } from '@geogame/shared';
import type { Tier } from '@geogame/shared';
import { useState } from 'react';

import { navigate } from '../lib/router';
import { points, questionCount, tierLabel, todayKey } from '../lib/format';
import { useGame } from '../store/gameStore';
import { dailyKey, readLocal } from '../store/storage';

export function DailyScreen(): React.JSX.Element {
  const startMatch = useGame((s) => s.startMatch);
  const storedTier = useGame((s) => s.tier);
  const lastError = useGame((s) => s.lastError);
  const [tier, setTier] = useState<Tier>(storedTier);
  const gameMode = useGame((s) => s.gameMode);

  const date = todayKey();
  const previous = readLocal(dailyKey(date, tier, gameMode));

  return (
    <main className="gg-screen gg-scroll">
      <header className="gg-hero">
        <p className="gg-subtitle">{date}</p>
        <h1 className="gg-title">Daily challenge</h1>
        <p className="gg-subtitle">
          One seed for everybody, one attempt. Same {questionCount(TIER_CONFIG[tier].ladderDepth)}
          questions, same countries, same clock.
        </p>
      </header>

      <section className="gg-section">
        <h2 className="gg-section-title">Tier</h2>
        <div className="gg-course-row">
          {/* Expert is a Countries difficulty; see PlayScreen. */}
          {TIERS.filter((t) => !countryPromptIsClue(t) || gameMode === 'country').map((t) => (
            <button
              key={t}
              type="button"
              className="gg-chip"
              data-selected={t === tier ? 'true' : 'false'}
              onClick={() => setTier(t)}
            >
              {tierLabel(t)}
            </button>
          ))}
        </div>
      </section>

      {previous ? (
        <section className="gg-section">
          <div className="gg-daily-done">
            <span className="gg-daily-done-label">Already played today</span>
            <span className="gg-daily-done-score tabular-nums">
              {points(Number(previous) || 0)}
            </span>
          </div>
          <p className="gg-note">Come back tomorrow for a fresh seed.</p>
        </section>
      ) : null}

      {lastError?.code === 'daily_already_played' ? (
        <p className="gg-note gg-note-error">{lastError.message}</p>
      ) : null}

      <section className="gg-section gg-actions">
        <button
          type="button"
          className="gg-btn gg-btn-primary gg-btn-xl"
          onClick={() => startMatch({ mode: 'daily', tier })}
        >
          {previous ? 'Play again (server decides)' : 'Start the daily'}
        </button>
        <button type="button" className="gg-btn" onClick={() => navigate({ name: 'home' })}>
          Back
        </button>
      </section>
    </main>
  );
}
